import {
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigationType,
} from 'react-router-dom';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import AddContributionScreen from './screens/AddContributionScreen';
import HomeScreen from './screens/HomeScreen';
import ScanScreen from './screens/ScanScreen';
import ThankYouScreen from './screens/ThankYouScreen';

/** Keep in sync with the stack animation duration in App.css. */
const ROUTE_TRANSITION_MS = 462;

function routeDepth(pathname = '') {
  if (pathname === '/thank-you') return 3;
  if (pathname === '/add-contribution') return 2;
  if (pathname === '/scan') return 1;
  return 0;
}

function isBackNavigation(fromPath, toPath, navigationType) {
  if (navigationType === 'POP') return true;
  return routeDepth(toPath) < routeDepth(fromPath);
}

function renderAppRoutes(location) {
  return (
    <Routes location={location}>
      <Route path="/" element={<HomeScreen />} />
      <Route path="/scan" element={<ScanScreen />} />
      <Route path="/add-contribution" element={<AddContributionScreen />} />
      <Route path="/thank-you" element={<ThankYouScreen />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function AppRoutes() {
  const location = useLocation();
  const navigationType = useNavigationType();
  const displayedRef = useRef(location);
  const [displayed, setDisplayed] = useState(location);
  const [outgoing, setOutgoing] = useState(null);
  const [direction, setDirection] = useState('forward');

  useLayoutEffect(() => {
    if (location.pathname === displayedRef.current.pathname) {
      displayedRef.current = location;
      setDisplayed(location);
      return;
    }

    const goingBack = isBackNavigation(
      displayedRef.current.pathname,
      location.pathname,
      navigationType
    );
    setDirection(goingBack ? 'back' : 'forward');
    setOutgoing(displayedRef.current);
    displayedRef.current = location;
    setDisplayed(location);
  }, [location, navigationType]);

  useEffect(() => {
    if (!outgoing) return undefined;
    const timer = window.setTimeout(() => setOutgoing(null), ROUTE_TRANSITION_MS);
    return () => window.clearTimeout(timer);
  }, [outgoing, displayed.pathname]);

  return (
    <div className="routeTransitionHost">
      {outgoing ? (
        <div
          key={`out-${outgoing.pathname}`}
          className={`routeTransition routeTransition--exit-${direction}`}
          aria-hidden="true"
        >
          {renderAppRoutes(outgoing)}
        </div>
      ) : null}
      <div
        key={`in-${displayed.pathname}`}
        className={[
          'routeTransition',
          outgoing ? `routeTransition--enter-${direction}` : '',
        ]
          .filter(Boolean)
          .join(' ')}
      >
        {renderAppRoutes(displayed)}
      </div>
    </div>
  );
}

export default AppRoutes;
